// VENDORED from software-architect-ai/templates/api-axios-instance.ts — DO NOT EDIT.
// Extend via createApiClient config in src/api/client.ts. Re-vendor on template change.
/**
 * CANONICAL axios instance factory — React fleet HTTP layer.
 * See software-architect-ai/http-client-strategy.md.
 *
 * EXTEND THIS, DON'T FORK IT. A repo creates its client by calling
 * `createApiClient(config)` with repo-specific endpoints, an in-memory token
 * store, path predicates, and (optionally) a tenant context. The four
 * interceptors (csrf / bearer / silent-refresh / error-normalize) are
 * centralized here — repos supply config, never copy interceptor logic.
 *
 * Pin: axios ^1.16.0. Tokens are IN MEMORY ONLY (no localStorage/sessionStorage).
 */
import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

/**
 * Marks a request as ineligible for refresh-and-retry. Set on the factory's OWN internal
 * auth-plumbing calls (the refresh POST and the CSRF fetch).
 *
 * Why this is load-bearing, not tidiness: without it, a refresh POST that itself returns 401
 * re-enters the response interceptor, which calls runRefresh() again, sees `refreshInFlight`
 * still pending, and awaits the very promise that is waiting on this request — a circular
 * wait. The result is a PERMANENT HANG: onSessionExpired() never fires, no redirect, and the
 * original caller's promise never settles. A dead session must fail fast, not hang.
 */
type NoRetry = { _retry?: boolean };

/* ---------------------------------------------------------------- error model */

/** Extend this union per repo with the backend's known codes. Unknown -> 'UNKNOWN'. */
export type ErrorCode =
  | 'SESSION_EXPIRED'
  | 'CSRF_VALIDATION_FAILED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN'
  | (string & {}); // allow repo-specific codes without losing autocomplete

export class ApiError extends Error {
  // `override` is required under `noImplicitOverride` (fleet-mandated); harmless without it.
  override readonly name = 'ApiError';
  constructor(
    readonly code: ErrorCode,
    readonly status: number,
    readonly requestId?: string,
    // `message?: string | undefined` (not `message?: string`) so callers may pass an explicitly
    // undefined message under `exactOptionalPropertyTypes` — a details bag legitimately carries
    // "known absent". Widening only; existing callers are unaffected.
    // `backend` carries the backend envelope's `error.details` bag verbatim — on
    // VALIDATION_FAILED that is `{ fields: { <field>: <message> } }` — so a form can map
    // messages onto fields. It is set whenever the envelope carries one, regardless of whether
    // the code was recognised; `raw` keeps its original meaning ("we could not classify this").
    // Additive: nothing existing reads a key that changes. (Fleet change 2026-09-20; requested
    // independently by two consoles that had each worked around its absence.)
    readonly details: {
      message?: string | undefined;
      raw?: unknown;
      backend?: Record<string, unknown> | undefined;
    } = {},
  ) {
    super(`API error: ${code}`);
  }
}

interface BackendEnvelope {
  success?: false;
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
}

/* --------------------------------------------------------------------- config */

export interface ApiClientConfig {
  /** Single base URL, OR a per-path resolver for multi-base surfaces (admin vs auth). */
  baseUrl?: string;
  resolveBaseUrl?: (path: string) => string;

  /** Per-path credential posture (cookies / credentials mode). Default true, preserving the
   *  historical always-credentialed behavior. Return false for bases that authenticate by
   *  bearer token alone — a bearer-only API correctly omits Access-Control-Allow-Credentials,
   *  and a credentialed request against it fails CORS at the browser. Keyed on the path of the
   *  request being issued (like resolveBaseUrl/bearer/csrf), so the silent refresh against the
   *  auth base stays credentialed naturally. */
  sendCredentials?: (path: string) => boolean;

  /** In-memory token store. The factory NEVER touches storage directly. */
  tokens: {
    getAccess: () => string | null;
    setAccess: (token: string | null, expiresAt?: number | null) => void;
    getCsrf: () => string | null;
    setCsrf: (token: string | null) => void;
  };

  /** Attach Authorization: Bearer on these paths. */
  bearer: { protected: (path: string) => boolean };

  /** CSRF policy. */
  csrf: {
    protected: (path: string, method: string) => boolean;
    /** 'cookie-echo' (same-origin auth surface) | 'store-token' (cross-origin surface). */
    mode: (path: string) => 'cookie-echo' | 'store-token';
    cookieName?: string; // default 'journix_csrf'
    fetchPath?: string; // default '/api/auth/csrf' (cookie-echo fallback)
  };

  /** Silent refresh endpoint (flat body: { accessToken, expiresAt?, csrfToken? }). */
  refresh: { path: string };

  /** Called when refresh fails — repo signs out / redirects to login. */
  onSessionExpired: () => void;

  /** OPTIONAL. Omit to rely on server-derived tenancy (the default). */
  getTenantContext?: () => { tenantId?: string } | null;

  /** Generate a request id (uuid). Repo supplies its own. */
  generateRequestId?: () => string;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/* --------------------------------------------------------------------- factory */

export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const instance = axios.create({ withCredentials: true });
  const reqId = config.generateRequestId ?? (() => crypto.randomUUID());
  const cookieName = config.csrf.cookieName ?? 'journix_csrf';
  const csrfFetchPath = config.csrf.fetchPath ?? '/api/auth/csrf';

  let refreshInFlight: Promise<boolean> | null = null;
  let csrfFetchInFlight: Promise<string | null> | null = null;

  /* [1+2+3] one request interceptor, deterministic order: base+headers -> bearer -> csrf */
  instance.interceptors.request.use(async (req) => {
    const path = pathOf(req);
    const method = (req.method ?? 'get').toUpperCase();

    // [1] base URL + credential posture + standard headers + optional tenant context
    // Assign only when a base is actually configured: under `exactOptionalPropertyTypes`,
    // `baseURL?: string` rejects an explicit undefined. axios.create() sets no baseURL, so
    // skipping the assignment leaves it undefined exactly as before — behavior-preserving.
    const resolvedBase = config.resolveBaseUrl ? config.resolveBaseUrl(path) : config.baseUrl;
    if (resolvedBase !== undefined) req.baseURL = resolvedBase;
    req.withCredentials = config.sendCredentials ? config.sendCredentials(path) : true;
    // JSON is the default, not a mandate: a caller that sets its own `Accept` (an XLSX export,
    // an ICS calendar) keeps it. Without this guard the interceptor overwrote the per-call
    // header and content negotiation answered 406. `Content-Type` is deliberately NOT set
    // here — axios's default transformer sets it for JSON bodies and omits it for FormData so
    // the browser supplies the multipart boundary; forcing it would break uploads.
    if (!req.headers.has('Accept')) req.headers.set('Accept', 'application/json');
    req.headers.set('X-Request-ID', reqId());
    const tenant = config.getTenantContext?.();
    if (tenant?.tenantId) req.headers.set('X-Tenant-ID', tenant.tenantId);

    // [2] bearer
    if (config.bearer.protected(path)) {
      const token = config.tokens.getAccess();
      if (token) req.headers.set('Authorization', `Bearer ${token}`);
    }

    // [3] csrf (two modes)
    if (config.csrf.protected(path, method) && MUTATING.has(method)) {
      if (config.csrf.mode(path) === 'cookie-echo') {
        const token = readCookie(cookieName) ?? (await fetchCsrf());
        if (token) {
          req.headers.set('X-CSRF-Token', token);
          if (isPlainObject(req.data)) req.data = { ...req.data, csrfToken: token }; // body echo
        }
      } else {
        const token = config.tokens.getCsrf(); // store-token (cross-origin): header only
        if (token) req.headers.set('X-CSRF-Token', token);
      }
    }
    return req;
  });

  /* [4a + 4b] one response interceptor: try refresh+retry, else normalize-and-throw */
  instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError<BackendEnvelope>) => {
      const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
      const status = error.response?.status ?? 0;
      const backendCode = error.response?.data?.error?.code;
      const path = original ? pathOf(original) : '';
      const method = (original?.method ?? 'get').toUpperCase();

      // [4a] silent refresh + single retry (bounded by _retry).
      // `!original._retry` does double duty: it bounds the retry to one attempt AND excludes
      // the factory's own internal calls, which are issued with `_retry: true` pre-set (see
      // NoRetry). Do not "simplify" either half away — dropping the pre-set flag on the
      // refresh POST reintroduces a permanent deadlock on a dead session.
      const refreshable =
        !!original &&
        !original._retry &&
        (status === 401 ||
          (status === 403 &&
            backendCode === 'INVALID_CSRF' &&
            config.csrf.mode(path) === 'store-token' &&
            MUTATING.has(method)));
      if (refreshable) {
        original._retry = true;
        const ok = await runRefresh();
        if (ok) return instance(original);
        config.onSessionExpired();
        throw new ApiError('SESSION_EXPIRED', 401, idOf(error));
      }

      // [4b] error normalization
      throw normalize(error, status, backendCode);
    },
  );

  /* ---- helpers ---- */

  async function runRefresh(): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const { data } = await instance.post<{
          accessToken?: string;
          expiresAt?: number | null;
          csrfToken?: string | null;
        }>(config.refresh.path, {}, {
          headers: { 'X-Refresh': '1' },
          // MUST stay set — see NoRetry. A 401 here means the session is genuinely dead; it
          // falls through to normalize-and-throw, is caught below, and returns false, so the
          // ORIGINAL request's handler runs onSessionExpired() and throws SESSION_EXPIRED.
          _retry: true,
        } as AxiosRequestConfig & NoRetry);
        if (!data?.accessToken) return false;
        config.tokens.setAccess(data.accessToken, data.expiresAt ?? null);
        config.tokens.setCsrf(data.csrfToken ?? null); // authoritative for store-token surfaces
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  async function fetchCsrf(): Promise<string | null> {
    if (csrfFetchInFlight) return csrfFetchInFlight;
    csrfFetchInFlight = instance
      // Also NoRetry: this fetch is awaited from INSIDE the request interceptor, so letting a
      // 401 here kick off a refresh would nest auth plumbing inside auth plumbing. It already
      // degrades correctly — `.catch(() => null)` below — so failing fast is the right shape.
      .get<{ csrfToken?: string }>(csrfFetchPath, { _retry: true } as AxiosRequestConfig & NoRetry)
      .then((r) => r.data?.csrfToken ?? null)
      .catch(() => null)
      .finally(() => {
        csrfFetchInFlight = null;
      });
    return csrfFetchInFlight;
  }

  return instance;
}

/* ----------------------------------------------------------- module helpers */

function normalize(error: AxiosError<BackendEnvelope>, status: number, backendCode?: string): ApiError {
  if (!error.response) {
    return new ApiError('NETWORK_ERROR', 0, idOf(error), { message: error.message });
  }
  const code: ErrorCode = backendCode ?? 'UNKNOWN';
  return new ApiError(code, status, idOf(error), {
    message: error.response.data?.error?.message,
    backend: error.response.data?.error?.details,
    raw: backendCode ? undefined : error.response.data,
  });
}

function pathOf(req: { url?: string; baseURL?: string }): string {
  const u = req.url ?? '';
  if (u.startsWith('http')) {
    try {
      return new URL(u).pathname;
    } catch {
      return u;
    }
  }
  return u;
}

function idOf(error: AxiosError): string | undefined {
  const h = error.config?.headers?.['X-Request-ID'];
  return typeof h === 'string' ? h : undefined;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${esc}=([^;]+)`));
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
